// Product page: the pack switch, the photo viewer, the crop chips, the free-delivery line, the docked ADD bar,
// the video player and the similar-products fallback.
// Liquid renders the price and the ADD/stepper once per pack; a pack change only shows that pack's copy,
// so no money is formatted here. assets/cart.js does the adding, as on the cards.
const REQUEST_TIMEOUT_MS = 15000;

const packs = document.querySelector('[data-packs]');

function selectPack(radio) {
  document.querySelectorAll('[data-variant-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.variantPanel !== radio.value;
  });
}

if (packs) {
  packs.addEventListener('change', (event) => {
    const radio = event.target.closest('[data-pack]');
    if (!radio) return;
    selectPack(radio);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', radio.value);
    window.history.replaceState(window.history.state, '', url);
  });
  // Some browsers restore a different pill after a reload: follow the pill the shopper sees.
  const checked = packs.querySelector('[data-pack]:checked');
  const shown = document.querySelector('[data-variant-panel]:not([hidden])');
  if (checked && checked.value !== shown?.dataset.variantPanel) selectPack(checked);
}

async function loadRecommendations(block) {
  try {
    // A plain GET: an "Accept: application/json" header would make Shopify ignore section_id.
    const response = await fetch(block.dataset.recommendationsUrl, { signal: AbortSignal.timeout?.(REQUEST_TIMEOUT_MS) });
    if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
    const html = await response.text();
    const cards = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
    if (!cards?.children.length) {
      block.hidden = true;
      return;
    }
    block.querySelector('[data-similar-track]').replaceChildren(...cards.children);
    block.removeAttribute('aria-busy');
    // assets/cart.js then shows the cart quantities on the new cards.
    document.dispatchEvent(new CustomEvent('products:appended'));
  } catch (error) {
    block.hidden = true;
    console.error('Could not load similar products', error);
  }
}

// Straight away, not when the rail scrolls into view: a shopper who jumps past it (a fling, the End key,
// coming back to the bottom of the page) never crossed an observer, and the rail stayed empty. One small
// request; the cards' images still load lazily.
const similar = document.querySelector('[data-recommendations-url]');
if (similar) loadRecommendations(similar);

// The video card (snippets/product-video.liquid) shows a thumbnail; the YouTube player (privacy mode)
// loads only after the tap, in the same 16:9 box, so nothing below moves.
document.addEventListener('click', (event) => {
  const facade = event.target.closest('[data-video-id]');
  if (!facade) return;
  const player = document.createElement('iframe');
  player.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(facade.dataset.videoId)}?autoplay=1&rel=0`;
  player.title = facade.dataset.videoTitle;
  player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  player.allowFullscreen = true;
  player.className = 'product-video__player';
  facade.replaceWith(player);
  player.focus();
});

// A plain left click: ctrl/cmd-click and middle-click keep opening the link in a new tab.
const isPlainClick = (event) => event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

// Photo viewer (#ProductZoom in sections/main-product.liquid): a tap on a photo opens every photo of the
// product over the page, one under the other, at the one tapped, instead of leaving the shop for the bare
// picture. The photos load only when the viewer opens (lazy, inside a closed dialog). The viewer takes a
// history step, so the phone's Back button closes it as shoppers expect; closing it another way (the
// button, a tap beside the photo, Esc) takes that step back again.
const zoom = document.getElementById('ProductZoom');
if (zoom) {
  const links = [...document.querySelectorAll('.product__zoom')];
  const photos = links.map((link) => {
    const img = document.createElement('img');
    img.className = 'zoom__img';
    img.loading = 'lazy';
    img.src = link.href;
    img.alt = link.querySelector('img')?.alt || '';
    return img;
  });
  zoom.prepend(...photos);
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.product__zoom');
    if (!link || !isPlainClick(event)) return;
    event.preventDefault();
    zoom.classList.remove('is-full');
    zoom.showModal();
    photos[links.indexOf(link)]?.scrollIntoView({ block: 'start' });
    window.history.pushState({ productZoom: true }, '');
  });
  zoom.addEventListener('click', (event) => {
    if (!event.target.matches('.zoom__img')) {
      zoom.close();
      return;
    }
    zoom.classList.toggle('is-full');
    event.target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
  window.addEventListener('popstate', () => {
    if (zoom.open) zoom.close();
  });
  zoom.addEventListener('close', () => {
    if (window.history.state?.productZoom) window.history.back();
  });
}

// Crop chips (snippets/product-answers.liquid): a tap chooses the crop here, showing its pests, dose and
// "Also for" row (every element with its data-crop); without JavaScript the chip opens the crop's collection.
document.addEventListener('click', (event) => {
  const chip = event.target.closest('[data-crop-chip]');
  if (!chip || !isPlainClick(event)) return;
  event.preventDefault();
  document.querySelectorAll('[data-crop-chip]').forEach((other) => {
    if (other === chip) other.setAttribute('aria-current', 'true');
    else other.removeAttribute('aria-current');
  });
  document.querySelectorAll('[data-crop]').forEach((panel) => {
    panel.hidden = panel.dataset.crop !== chip.dataset.cropChip;
  });
});

// Free-delivery line (snippets/product-free-delivery.liquid). Its data-cart-sections makes assets/cart.js
// bring a fresh copy, formatted by Liquid, in the reply of every cart change; when that reply had none
// (a failed change), ask Shopify for it.
const FREE_SECTION = 'product-free-delivery';
async function freeDeliveryHtml(sections) {
  if (sections?.[FREE_SECTION]) return sections[FREE_SECTION];
  const response = await fetch(`${window.location.pathname}?sections=${FREE_SECTION}`, { signal: AbortSignal.timeout?.(REQUEST_TIMEOUT_MS) });
  if (!response.ok) throw new Error(`Free-delivery request failed (${response.status})`);
  return (await response.json())[FREE_SECTION];
}
if (document.querySelector('[data-free-delivery]')) {
  document.addEventListener('cart:updated', async (event) => {
    try {
      const html = await freeDeliveryHtml(event.detail?.sections);
      const fresh = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-free-delivery]');
      const current = document.querySelector('[data-free-delivery]');
      if (fresh && current) current.replaceWith(fresh);
    } catch (error) {
      console.error('Could not refresh the free-delivery line', error);
    }
  });
}

// Docked bar (snippets/product-dock.liquid): shows whenever the chosen pack's own ADD bar is off the
// screen, above or below it (on a small phone with many packs the ADD can sit below the first screen),
// and hides while that ADD is in view. Each pack has its own bar; changing pack shows another one, which
// the observer reports too. Older browsers without IntersectionObserver keep the buy box only.
const dock = document.querySelector('[data-product-dock]');
const buyBox = document.querySelector('[data-product-buy]');
if (dock && buyBox && window.IntersectionObserver) {
  const bars = buyBox.querySelectorAll('.product-buy__bar');
  const shownBar = () => buyBox.querySelector('[data-variant-panel]:not([hidden]) .product-buy__bar') || buyBox;
  const watcher = new IntersectionObserver(() => {
    const box = shownBar().getBoundingClientRect();
    dock.hidden = box.bottom > 0 && box.top < window.innerHeight;
  });
  (bars.length ? bars : [buyBox]).forEach((bar) => watcher.observe(bar));
}
